import type { DashboardParams } from '@/hooks/JobBoardFilters/useJobBoardParams';
import { useJobBoardParams } from '@/hooks/JobBoardFilters/useJobBoardParams';
import { capitalizeFirstLetter } from '@/utils/cvGenerator';
import { getTextEllipsis } from '@/utils/getTextEllipsis';

import Button from '../Button';
import Text from '../Text';
import Chip from './Chip';

type FilterValue = DashboardParams[keyof DashboardParams];

const HIDDEN_FILTER_KEYS = ['ordering', 'company'];

const getBeautifyLabel = (text: string) =>
  text
    .split('_')
    .map((valuePart) => capitalizeFirstLetter(valuePart))
    .join(' ');

const LABEL_FORMATTERS: Partial<Record<string, (value: string) => string>> = {
  salaryLower: (value) => `Salary lower: $${getBeautifyLabel(value)}`,
  salaryUpper: (value) => `Salary upper: $${getBeautifyLabel(value)}`,
  location: (value) => `Location: ${getTextEllipsis(value, 10)}`,
  search: (value) => `Search: ${getTextEllipsis(value, 10)}`,
};

const getFilterLabel = (key: string, value: FilterValue) => {
  if (typeof value === 'undefined') return undefined;
  return (
    LABEL_FORMATTERS[key]?.(value.toString()) ??
    getBeautifyLabel(value.toString())
  );
};

const AdvancedFilterChips = () => {
  const { resetParams, deleteParam, params } = useJobBoardParams();

  const activeFilters = Object.entries(params)
    .flatMap(([key, value]) =>
      Array.isArray(value)
        ? value.map((singleValue) => ({ key, value: singleValue }))
        : { key, value }
    )
    .filter(
      ({ key, value }) =>
        value !== undefined && value !== '' && !HIDDEN_FILTER_KEYS.includes(key)
    )
    .map(({ key, value }) => ({
      key,
      value,
      label: getFilterLabel(key, value),
    }))
    .filter(({ label }) => !!label);

  if (activeFilters.length === 0) return null;

  return (
    <div
      role="list"
      className="flex h-full w-full flex-row flex-wrap items-center justify-start gap-3 border-t py-3"
    >
      {activeFilters.map(({ key, value, label }) => (
        <div key={`${key}-${value}`} role="listitem" title={label}>
          <Chip
            variant="gray"
            className="items-center !rounded-2xl !border-jb-primary-300 bg-jb-primary-100 !py-px !pr-2 !text-jb-primary-500 transition-colors animate-in fade-in hover:bg-jb-primary-200"
            label={label ?? ''}
            suffixIcon="material-symbols-light:close"
            suffixIconClassName="text-[16px] text-jb-primary-500 transition-colors hover:text-jb-primary-600"
            onSuffixIconClick={() => deleteParam(key, value?.toString())}
          />
        </div>
      ))}
      <Button
        type="button"
        variant="unstyled"
        size="xs"
        aria-label="Clear all filters"
        onClick={resetParams}
        className="ml-2 rounded focus-visible:ring-2 focus-visible:ring-jb-primary-300 focus-visible:ring-offset-2"
      >
        <Text
          className="font-semibold text-jb-primary-400 transition-colors hover:text-jb-primary-500"
          variant="body-small"
        >
          Clear all
        </Text>
      </Button>
    </div>
  );
};

export default AdvancedFilterChips;
